// src/oura/components/ItemThread.tsx
//
// One item, one thread. Shared by the merchant's page and the staff inbox, so
// both sides read the same conversation the same way. The thread is the
// conversation; the item's status lives on the item and is not touched here.

import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import { CalendarIcon, CheckIcon, PaperclipIcon, PencilLineIcon, PhoneIcon, RefreshCwIcon, SendIcon } from "lucide-react";
import { uploadFile, UPLOAD_ACCEPT, UPLOAD_MAX_BYTES } from "@service/aeo/clientReview";
import type { Audience, PackItem, Thread, ThreadMessage } from "@service/aeo/clientReview";
import { FOCUS_RING, LATE_TEXT, PillButton, SURFACE, TYPE, useEscapeLayer } from "@oura/ui";
import { useViewAs } from "@hook/use-view-as";
import { useWritesPaused } from "@context/maintenance";
import { cn } from "../lib/cn";
import { useRadioGroup } from "../lib/useRadioGroup";
import { keepUnsentReply, readUnsentReply } from "./unsentReplies";

const CALL_TIMES = ["This week", "Next week", "Mornings", "Afternoons"] as const;
type CallTime = (typeof CALL_TIMES)[number];

const FIELD = cn(TYPE.tb, "block w-full rounded-lg border-0 bg-white px-2.5 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600");
const STAMP = cn(TYPE.tm, "shrink-0 tabular-nums text-gray-500");

function when(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" });
}

function Avatar({ kind, name }: { kind: ThreadMessage["authorKind"]; name: string }) {
    const initials = name.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "?";
    return (
        <span aria-hidden className={cn(TYPE.tm, "flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-semibold text-white",
            kind === "client" ? "bg-violet-600" : kind === "oura" ? "bg-gray-700" : "bg-blue-600")}>
            {kind === "oura" ? "O" : initials}
        </span>
    );
}

function SystemLine({ m }: { m: ThreadMessage }) {
    const Icon = m.kind === "version" ? RefreshCwIcon : m.kind === "call_request" ? PhoneIcon : m.kind === "suggested_edit" ? PencilLineIcon : CheckIcon;
    return (
        <div className={cn(SURFACE.inner, TYPE.tm, "my-1.5 ml-9 flex items-center gap-2 text-gray-600")}>
            <Icon aria-hidden size={14} className="shrink-0 text-gray-400" />
            <span className="min-w-0 flex-1 break-words">{m.body}</span>
            <span className={STAMP}>{when(m.at)}</span>
        </div>
    );
}

// Tabbable controls; a radio off the tab stop (tabindex -1) is skipped.
const TABBABLE = 'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled])';

/**
 * A small form that replaces the reply box keeps its opener in reach: focus
 * goes to the form's first control when it opens, the first Escape closes the
 * form (not the drawer around it), and focus comes back to the button that
 * opened it, which the form had replaced, when it closes. Returns the id to
 * give that button.
 */
function useInlineForm(open: boolean, close: () => void, form: RefObject<HTMLElement | null>): string {
    const openerId = useId();
    const wasOpen = useRef(open);
    useEscapeLayer(close, open);
    useEffect(() => {
        if (open && !wasOpen.current) form.current?.querySelector<HTMLElement>(TABBABLE)?.focus();
        if (!open && wasOpen.current) document.getElementById(openerId)?.focus();
        wasOpen.current = open;
    }, [open, openerId, form]);
    return openerId;
}

export function ItemThread({
    item,
    thread,
    me,
    myName,
    onPost,
    onRequestCall,
    onAcceptEdit,
    onResolve,
    compact,
    businessId,
    sendLabel = "Send",
    sendNote,
    skipIds,
}: {
    item: PackItem;
    thread: Thread | null;
    me: Audience;
    myName: string;
    /** The business the thread belongs to, so an attachment can be uploaded. */
    businessId: string;
    onPost: (input: { body: string; fileKey?: string | null; suggestedEdit?: { before: string; after: string } | null }) => Promise<void>;
    /** Merchant only. Absent on the staff side. */
    onRequestCall?: (preference: string, note: string) => Promise<void>;
    /** Staff only: accept a merchant's suggested edit as the final wording. */
    onAcceptEdit?: (messageId: string) => Promise<void>;
    onResolve?: () => Promise<void>;
    compact?: boolean;
    /** The reply button's words, where a plain "Send" could be read as answering. */
    sendLabel?: string;
    /** One line beside the reply button, saying what sending does and does not do. */
    sendNote?: ReactNode;
    /** Messages the drawer already quotes above the thread: not listed twice. */
    skipIds?: ReadonlySet<string>;
}) {
    // Staff viewing as the client cannot write (#4112); the server refuses it too.
    const { active: readOnly } = useViewAs();
    const writesPaused = useWritesPaused();
    // What was typed and not sent last time this item's thread was open.
    const [body, setBody] = useState(() => readUnsentReply(me, item.id)?.body ?? "");
    const [file, setFile] = useState<File | null>(() => readUnsentReply(me, item.id)?.file ?? null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [edit, setEdit] = useState(item.proposed ?? "");
    const [calling, setCalling] = useState(false);
    const [pref, setPref] = useState<CallTime>("This week");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const editForm = useRef<HTMLDivElement>(null);
    const callForm = useRef<HTMLDivElement>(null);
    const callTitleId = useId();
    const prefLabelId = useId();

    useEffect(() => {
        keepUnsentReply(me, item.id, { body, file });
    }, [me, item.id, body, file]);

    const cancelEdit = () => {
        setEditing(false);
        setEdit(item.proposed ?? "");
    };
    const editOpener = useInlineForm(editing, cancelEdit, editForm);
    const callOpener = useInlineForm(calling, () => setCalling(false), callForm);
    const radio = useRadioGroup(CALL_TIMES, pref, setPref);

    const messages = (thread?.messages ?? []).filter((m) => !skipIds?.has(m.id));
    const send = async () => {
        if (!body.trim() && !file) return;
        setBusy(true);
        setFileError(null);
        try {
            // Upload first, post second. The server stores a fileKey; the name
            // alone was accepted by this form and dropped by the API, so an
            // attachment looked sent and arrived as nothing.
            let fileKey: string | null = null;
            if (file) fileKey = (await uploadFile(businessId, file)).fileKey;
            await onPost({ body: body.trim(), fileKey });
            setBody("");
            setFile(null);
        } catch {
            setFileError("That did not send. Check the file and try again.");
        } finally { setBusy(false); }
    };
    const sendEdit = async () => {
        if (!edit.trim() || edit === item.proposed) return;
        setBusy(true);
        try { await onPost({ body: "Suggested an edit to the wording.", suggestedEdit: { before: item.proposed ?? "", after: edit } }); setEditing(false); }
        finally { setBusy(false); }
    };
    const requestCall = async () => {
        if (!onRequestCall) return;
        setBusy(true);
        try { await onRequestCall(pref, note.trim()); setCalling(false); setNote(""); }
        finally { setBusy(false); }
    };

    return (
        <div className={cn("border-t border-gray-100", compact ? "px-4 py-3" : "px-5 py-4")}>
            <div className="mb-1 flex items-center justify-between">
                <p className={cn(TYPE.lab, "m-0")}>Conversation{messages.length > 0 && <span className="tabular-nums"> · {messages.length}</span>}</p>
                {thread?.status === "resolved" && <span className={cn(TYPE.tm, "text-emerald-700")}>Resolved</span>}
                {thread?.status === "open" && me === "staff" && onResolve && messages.length > 0 && (
                    <PillButton variant="text" writes onClick={() => void onResolve()}>Mark resolved</PillButton>
                )}
            </div>

            {messages.length === 0 && <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>{me === "client" ? "Ask anything. We reply here." : "No messages yet."}</p>}

            <div className="divide-y divide-gray-100">
                {messages.map((m) => m.kind !== "message" && m.kind !== "suggested_edit" ? (
                    <SystemLine key={m.id} m={m} />
                ) : (
                    <div key={m.id} className="flex gap-2.5 py-2.5">
                        <Avatar kind={m.authorKind} name={m.authorName} />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className={cn(TYPE.tc, "font-medium text-gray-900")}>{m.authorName}{m.authorKind === "staff" ? ", ourteam" : ""}</span>
                                <span className={STAMP}>{when(m.at)}</span>
                            </div>
                            {m.body && <p className={cn(TYPE.tb, "m-0 mt-0.5 whitespace-pre-line break-words text-gray-700")}>{m.body}</p>}
                            {m.fileName && (
                                <span className={cn(SURFACE.inner, TYPE.tm, "mt-1.5 inline-flex items-center gap-1.5 py-1 text-gray-700")}><PaperclipIcon aria-hidden size={12} className="text-gray-500" />{m.fileName}</span>
                            )}
                            {m.suggestedEdit && (
                                <div className={cn(TYPE.tc, "mt-2 rounded-xl bg-blue-50 px-3 py-2.5")}>
                                    <p className={cn(TYPE.lab, "m-0 mb-1")}>Their wording</p>
                                    <p className="m-0 text-gray-900">{m.suggestedEdit.after}</p>
                                    {m.suggestedEdit.accepted ? (
                                        <p className={cn(TYPE.tm, "m-0 mt-1.5 inline-flex items-center gap-1 font-medium text-emerald-700")}><CheckIcon aria-hidden size={12} />Accepted as the final wording</p>
                                    ) : me === "staff" && onAcceptEdit ? (
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <PillButton variant="primary" size="sm" writes onClick={() => void onAcceptEdit(m.id)}>Accept their wording</PillButton>
                                            <span className={cn(TYPE.tm, "text-gray-500")}>Marks the item approved and records this text.</span>
                                        </div>
                                    ) : (
                                        <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>Waiting for ourteam to accept.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {editing ? (
                <div ref={editForm} className="mt-3 rounded-xl bg-white p-3 ring-2 ring-blue-600">
                    <p className={cn(TYPE.tm, "m-0 mb-1.5 text-gray-500")}>Change anything. We see exactly what you changed.</p>
                    <textarea
                        value={edit}
                        onChange={(e) => setEdit(e.target.value)}
                        rows={5}
                        aria-label={`Your wording for "${item.title}"`}
                        className={cn(TYPE.tb, "w-full resize-y border-0 p-0 text-gray-900 focus:outline-none focus:ring-0")}
                    />
                    <div className="mt-2 flex items-center gap-2 border-t border-gray-100 pt-2">
                        <PillButton variant="primary" size="sm" icon={SendIcon} busy={busy} writes disabled={edit === item.proposed} onClick={() => void sendEdit()}>Send</PillButton>
                        <PillButton size="sm" onClick={cancelEdit}>Cancel</PillButton>
                    </div>
                </div>
            ) : calling ? (
                <div ref={callForm} role="group" aria-labelledby={callTitleId} className="mt-3 rounded-xl bg-blue-50/60 p-3 ring-1 ring-inset ring-blue-100">
                    <p id={callTitleId} className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>A quick call about this</p>
                    <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-500")}>15 minutes, part of your plan. We reply with a time.</p>
                    <p id={prefLabelId} className="sr-only">When suits you</p>
                    <div role="radiogroup" aria-labelledby={prefLabelId} className="mt-2 flex flex-wrap gap-1.5">
                        {CALL_TIMES.map((p, i) => (
                            <button
                                key={p}
                                type="button"
                                {...radio(i)}
                                className={cn(
                                    TYPE.tm,
                                    "h-7 rounded-full px-3 font-medium transition-colors",
                                    pref === p ? "bg-blue-600 text-white" : "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50",
                                    FOCUS_RING,
                                )}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Anything to know before the call (optional)" aria-label="Anything to know before the call" className={cn(FIELD, "mt-2")} />
                    <div className="mt-2 flex gap-2">
                        <PillButton variant="primary" size="sm" icon={PhoneIcon} busy={busy} writes onClick={() => void requestCall()}>Send request</PillButton>
                        <PillButton size="sm" onClick={() => setCalling(false)}>Cancel</PillButton>
                    </div>
                </div>
            ) : (
                <div className="mt-3 rounded-xl bg-white p-2.5 ring-1 ring-inset ring-gray-200 focus-within:ring-2 focus-within:ring-blue-600">
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        disabled={readOnly}
                        rows={2}
                        placeholder={`Write a reply as ${myName}`}
                        aria-label="Your reply"
                        className={cn(TYPE.tb, "w-full resize-none border-0 p-0.5 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0")}
                    />
                    {fileError && <p role="alert" className={cn(TYPE.tm, LATE_TEXT, "m-0 mt-1.5")}>{fileError}</p>}
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
                        <input
                            ref={fileInput}
                            type="file"
                            hidden
                            disabled={writesPaused}
                            accept={UPLOAD_ACCEPT}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                if (!f) return;
                                if (f.size > UPLOAD_MAX_BYTES) { setFileError("That file is over 20 MB."); return; }
                                setFileError(null);
                                setFile(f);
                            }}
                        />
                        <PillButton
                            size="sm"
                            icon={PaperclipIcon}
                            disabled={readOnly}
                            writes
                            onClick={() => fileInput.current?.click()}
                            aria-label={file ? `Attached: ${file.name}. Choose another file` : "Attach a file"}
                            className="max-w-[14rem]"
                        >
                            <span className="truncate">{file?.name ?? "Attach"}</span>
                        </PillButton>
                        {me === "client" && item.kind === "approve" && item.proposed && item.decision !== "approved" && (
                            <PillButton id={editOpener} size="sm" icon={PencilLineIcon} disabled={readOnly} onClick={() => setEditing(true)}>Edit the wording myself</PillButton>
                        )}
                        {me === "client" && onRequestCall && (
                            <PillButton id={callOpener} size="sm" icon={CalendarIcon} disabled={readOnly} writes onClick={() => setCalling(true)}>Talk to us</PillButton>
                        )}
                        <span className="flex-1" />
                        {sendNote && <span className={cn(TYPE.tm, "text-gray-500")}>{sendNote}</span>}
                        <PillButton variant="primary" size="sm" icon={SendIcon} busy={busy} writes disabled={readOnly || (!body.trim() && !file)} onClick={() => void send()}>
                            {sendLabel}
                        </PillButton>
                    </div>
                </div>
            )}
        </div>
    );
}
